import type { QueryEntityMetadata } from "@kodikas/fetchxml-builder";
import type { DataverseClient } from "./client";
import type {
  DataverseEntityDefinitionRecord,
  EntityDefinitionApiModel,
  EntityMetadataApiModel,
  ODataListResponse,
  DisplayNameNode,
} from "./types";

function displayName(displayNameNode: DisplayNameNode | undefined, fallback: string): string {
  return displayNameNode?.UserLocalizedLabel?.Label || fallback;
}

export async function loadEntityDefinitions(client: DataverseClient): Promise<DataverseEntityDefinitionRecord[]> {
  const response = await client.fetchJson<ODataListResponse<EntityDefinitionApiModel>>(
    "/EntityDefinitions?$filter=IsCustomizable/Value%20eq%20true&$select=MetadataId,LogicalName,SchemaName,DisplayName,EntitySetName,PrimaryIdAttribute",
  );

  return response.value
    .map((entity): DataverseEntityDefinitionRecord => ({
      metadataId: entity.MetadataId,
      logicalName: entity.LogicalName,
      schemaName: entity.SchemaName,
      entitySetName: entity.EntitySetName,
      primaryIdAttribute: entity.PrimaryIdAttribute,
      displayName: displayName(entity.DisplayName, entity.SchemaName),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export async function loadEntityMetadata(client: DataverseClient, logicalName: string): Promise<QueryEntityMetadata> {
  const escaped = logicalName.replace(/'/g, "''");
  const response = await client.fetchJson<EntityMetadataApiModel>(
    `/EntityDefinitions(LogicalName='${escaped}')?$select=LogicalName,DisplayName&$expand=Attributes($select=MetadataId,LogicalName,SchemaName,AttributeType,DisplayName,IsCustomizable),ManyToOneRelationships($select=MetadataId,SchemaName,ReferencedEntity,ReferencingAttribute,ReferencedAttribute,IsCustomizable),OneToManyRelationships($select=MetadataId,SchemaName,ReferencingEntity,ReferencingAttribute,ReferencedAttribute,IsCustomizable),ManyToManyRelationships($select=MetadataId,SchemaName,Entity1LogicalName,Entity2LogicalName,Entity1IntersectAttribute,Entity2IntersectAttribute,IntersectEntityName,IsCustomizable)`,
  );

  const attributes = response.Attributes.filter((item) => item.IsCustomizable?.Value)
    .map((item) => ({
      metadataId: item.MetadataId,
      logicalName: item.LogicalName,
      schemaName: item.SchemaName,
      attributeType: item.AttributeType,
      displayName: displayName(item.DisplayName, item.SchemaName),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  const manyToOneRelations = response.ManyToOneRelationships.filter((item) => item.IsCustomizable?.Value).map((item) => ({
    key: `m2o:${item.MetadataId}`,
    label: `${logicalName}.${item.ReferencingAttribute} -> ${item.ReferencedEntity}.${item.ReferencedAttribute} (${item.SchemaName})`,
    schemaName: item.SchemaName,
    relationType: "many-to-one" as const,
    targetEntityLogicalName: item.ReferencedEntity,
    fromAttribute: item.ReferencedAttribute,
    toAttribute: item.ReferencingAttribute,
  }));

  const oneToManyRelations = response.OneToManyRelationships.filter((item) => item.IsCustomizable?.Value).map((item) => ({
    key: `o2m:${item.MetadataId}`,
    label: `${logicalName}.${item.ReferencedAttribute} <- ${item.ReferencingEntity}.${item.ReferencingAttribute} (${item.SchemaName})`,
    schemaName: item.SchemaName,
    relationType: "one-to-many" as const,
    targetEntityLogicalName: item.ReferencingEntity,
    fromAttribute: item.ReferencingAttribute,
    toAttribute: item.ReferencedAttribute,
  }));

  const manyToManyRelations = response.ManyToManyRelationships
    .filter((item) => item.IsCustomizable?.Value !== false)
    .map((item) => {
      const isEntity1Side = item.Entity1LogicalName === logicalName;
      const peerLogicalName = isEntity1Side ? item.Entity2LogicalName : item.Entity1LogicalName;
      const localIntersectAttribute = isEntity1Side ? item.Entity1IntersectAttribute : item.Entity2IntersectAttribute;
      const peerIntersectAttribute = isEntity1Side ? item.Entity2IntersectAttribute : item.Entity1IntersectAttribute;

      return {
        key: `m2m:${item.MetadataId}`,
        label: `${logicalName} <-> ${peerLogicalName} via ${item.IntersectEntityName}.${localIntersectAttribute} (${item.SchemaName})`,
        schemaName: item.SchemaName,
        relationType: "many-to-many" as const,
        targetEntityLogicalName: item.IntersectEntityName,
        fromAttribute: localIntersectAttribute,
        toAttribute: localIntersectAttribute,
        manyToMany: {
          intersectEntityLogicalName: item.IntersectEntityName,
          peerEntityLogicalName: peerLogicalName,
          localIntersectAttribute,
          peerIntersectAttribute,
        },
      };
    });

  return {
    logicalName: response.LogicalName,
    displayName: displayName(response.DisplayName, response.LogicalName),
    attributes,
    relations: [...manyToOneRelations, ...oneToManyRelations, ...manyToManyRelations].sort((a, b) =>
      a.label.localeCompare(b.label),
    ),
  };
}
